

import React, { useState } from "react";
import PropTypes from 'prop-types';
import { IconButton, Tooltip, Box, Button, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import FileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import PdfIcon from '@mui/icons-material/PictureAsPdf';
import DocumentIcon from '@mui/icons-material/Description';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import colors from "assets/theme/base/colors";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import TimelineItem from "examples/Timeline/TimelineItem";
import { formatDate } from "utils/DateTimeConversion";
import MDButton from "components/MDButton";
import { useTheme } from "@mui/material/styles";
import { v2 as t, v2Tones } from "styles/tokens/v2";

function Attachments({ 
  attachments = [], 
  onDelete, 
  onDownload,
}) {
  const isV2 = useTheme()?.pearlUi === "v2";
  const [showUpload, setShowUpload] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    attachmentId: null
  });

  const getFileIcon = (fileType) => {
    if (fileType?.includes('image')) return <ImageIcon />;
    if (fileType?.includes('pdf')) return <PdfIcon />;
    if (fileType?.includes('document') || fileType?.includes('word')) return <DocumentIcon />;
    return <FileIcon />;
  };

  const getFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleUploadSuccess = (data) => {
    // You might want to trigger a refresh of the attachments list here
    console.log('Upload success:', data);
    setShowUpload(false);
  };

  const handleUploadError = (error) => {
    console.error('Upload error:', error);
    // Handle error appropriately - maybe show a notification
  };

  const handleDeleteClick = (attachmentId) => {
    setDeleteModal({
      open: true,
      attachmentId
    });
  };

  const handleDeleteConfirm = () => {
    onDelete(deleteModal.attachmentId);
    setDeleteModal({
      open: false,
      attachmentId: null
    });
  };

  const handleDeleteCancel = () => {
    setDeleteModal({
      open: false,
      attachmentId: null
    });
  };
  return (
    <>
    {/* v2: the card around this already pads its body. */}
    <MDBox p={isV2 ? 0 : 2}>
      {attachments.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            // py: 3,
            // px: 2,
          }}
        >
          <Box sx={{
            width: 50,
            height: 50,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.04)',
            mb: 1.5
          }}>
            <AttachFileIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
          </Box>
          <MDTypography variant="body2" color="text.secondary" align="center">
            No attachments to display.
          </MDTypography>
          {/* <MDTypography variant="caption" color="text.secondary" align="center" sx={{ mt: 0.5 }}>
            Add your first attachment by clicking the "Add Attachment" button above.
          </MDTypography> */}
        </Box>
      ) : (
        <MDBox
          sx={{
            maxHeight: '300px',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#555',
            },
          }}
        >
          {isV2 && attachments.map((attachment, index) => (
            // v2 (design-reference "Sales · Deal record"): file icon, name and
            // "size · date" on the left, quiet download and delete on the right.
            <Box
              key={attachment.id || index}
              sx={{ display: 'flex', gap: 1.5, alignItems: 'center', px: 1, py: 1, borderRadius: '8px', '&:hover': { backgroundColor: t.hover } }}
            >
              <Box sx={{ width: 32, height: 32, flexShrink: 0, borderRadius: '8px', display: 'grid', placeItems: 'center', bgcolor: t.surface2, border: `1px solid ${t.border}`, color: t.text2, '& svg': { fontSize: 17 } }}>
                {getFileIcon(attachment.file_type)}
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <MDTypography component="div" sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.4, color: t.text, overflowWrap: 'anywhere' }}>
                  {attachment.file_name}
                </MDTypography>
                <MDTypography component="div" sx={{ fontSize: 12, lineHeight: 1.4, color: t.text2 }}>
                  {getFileSize(attachment.file_size)} · {formatDate(attachment.created_at, 'datetime')}
                </MDTypography>
              </Box>
              <Tooltip title="Download">
                <IconButton size="small" aria-label={`Download ${attachment.file_name}`} onClick={() => onDownload(attachment)} sx={{ '& svg': { color: `${t.text2} !important` }, '&:hover': { backgroundColor: t.surface, '& svg': { color: `${t.link} !important` } } }}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  aria-label={`Delete ${attachment.file_name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(attachment.id);
                  }}
                  sx={{ '& svg': { color: `${t.text2} !important` }, '&:hover': { backgroundColor: v2Tones.danger.bg, '& svg': { color: `${v2Tones.danger.fg} !important` } } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
          {!isV2 && attachments.map((attachment, index) => (
            <TimelineItem
              key={attachment.id || index}
              color="info"
              icon={getFileIcon(attachment.file_type)}
              title={
                <MDBox display="flex" justifyContent="space-between" alignItems="center" width="100%">
                  <MDBox>
                    <MDTypography variant="button" fontWeight="regular"  sx={{ color: '#2c3e50' }}>
                      {attachment.file_name} 
                    <MDTypography component="span" variant="caption" color="text" ml={1} sx={{ color: '#7f8c8d' }}>{getFileSize(attachment.file_size)}</MDTypography>
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <Tooltip title="Download">
                      <IconButton
                        size="small"
                        onClick={() => onDownload(attachment)}
                        sx={{ color: 'info.main', mr: 1 }}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(attachment.id);
                      }}
                      sx={{ 
                        color: 'error.main',
                        '&:hover': {
                          backgroundColor: 'error.light',
                          color: 'white',
                        }
                      }}
                    >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </MDBox>
                </MDBox>
              }
              dateTime={formatDate(attachment.created_at, 'datetime')}
            />
          ))}
        </MDBox>
      )}
    </MDBox>


{/* Enhanced Delete Confirmation Modal */}
<Dialog
  open={deleteModal.open}
  onClose={handleDeleteCancel}
  PaperProps={{
    sx: {
      borderRadius: '8px',
      padding: 0,
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
      minWidth: '400px',
      overflow: 'hidden',
    }
  }}
>
  {/* Header */}
  <Box className="pearl-popup-header"
    sx={{
      backgroundColor: colors.popupHeader.background,
      px: 3,
      py: 2.5,
    }}
  >
    <MDTypography 
      variant="h6" 
      fontWeight="medium"
      sx={{ color: '#FFFFFF' }}
    >
      Delete Attachment?
    </MDTypography>
  </Box>

  {/* Content */}
  <DialogContent sx={{ p: 3, mt: 1 }}>
    <MDTypography 
      variant="body1" 
      sx={{ 
        color: '#34495e',
        lineHeight: 1.6,
        fontSize: '1rem'
      }}
    >
      Are you sure you want to delete this attachment? This action cannot be undone.
    </MDTypography>
  </DialogContent>

  {/* Actions */}
  <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: '12px' }}>
    <MDButton
      onClick={handleDeleteCancel}
      variant="outlined"
      color="secondary"
    >
      Cancel
    </MDButton>
    <MDButton
      onClick={handleDeleteConfirm}
      variant="gradient"
      color="error"
    >
      Delete
    </MDButton>
  </DialogActions>
</Dialog>
</>
  );
}

Attachments.propTypes = {
  attachments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      file_name: PropTypes.string,
      file_type: PropTypes.string,
      file_size: PropTypes.number,
      created_at: PropTypes.string,
    })
  ),
  onDelete: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  bucketName: PropTypes.string,
  folderPath: PropTypes.string,
};

export default Attachments;